import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { concatMap, of } from 'rxjs';

import { LoaderComponent } from 'src/app/components/widgets/loader/loader.component';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import { TextAreaFieldComponent } from 'src/app/components/form/textarea-field/textarea-field.component';
import { ProjectsFinanceAttachmentComponent } from '../projects-finance-attachment/projects-finance-attachment.component';
import { ProjectFinanceService } from 'src/app/services/api/project-finance.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { CommonService } from 'src/app/services/common.service';
import { RouteService } from 'src/app/services/route.service';
import {
  PROJECT_FINANCE_INVOICE_MAX_SIZE_BYTES,
  PROJECT_OTHER_COST_CATEGORY_LABELS,
  ProjectFinanceAttachment,
  ProjectFinanceOtherCost,
  ProjectFinanceOtherCostPayload,
  ProjectOtherCostCategory
} from 'src/app/models/entities/project-finance';

@Component({
  selector: 'projects-finance-other-cost-form',
  imports: [ReactiveFormsModule, LoaderComponent, InputFieldComponent, SelectFieldComponent, TextAreaFieldComponent, ProjectsFinanceAttachmentComponent],
  templateUrl: './projects-finance-other-cost-form.component.html',
  styleUrl: './projects-finance-other-cost-form.component.scss'
})
export class ProjectsFinanceOtherCostFormComponent implements OnInit {
  @Input() projectId!: number;
  @Input() projectFinanceOtherCostId: number | null = null;

  currentAttachment: ProjectFinanceAttachment | null = null;
  selectedFile: File | null = null;
  isLoading = false;
  isSaving = false;

  readonly categoryOptions: SelectOption[] = (Object.keys(PROJECT_OTHER_COST_CATEGORY_LABELS) as ProjectOtherCostCategory[])
    .map(category => ({ key: category, value: PROJECT_OTHER_COST_CATEGORY_LABELS[category] }));

  form = new FormGroup({
    category: new FormControl<ProjectOtherCostCategory>('TRAVEL', Validators.required),
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    description: new FormControl<string | null>(null, Validators.required),
    date: new FormControl<string | null>(null, Validators.required),
    notes: new FormControl<string | null>(null)
  });

  constructor(
    private projectFinanceService: ProjectFinanceService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private routeService: RouteService
  ) { }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.loadOtherCost();
    } else {
      this.form.controls.date.setValue(this.commonService.formatDateOnly(new Date()));
    }
  }

  get isEditMode(): boolean {
    return this.projectFinanceOtherCostId !== null;
  }

  getControl(name: keyof typeof this.form.controls): FormControl {
    return this.form.controls[name] as FormControl;
  }

  readonly validateReceiptFile = (file: File): string | null =>
    file.size > PROJECT_FINANCE_INVOICE_MAX_SIZE_BYTES ? 'The file exceeds the 10 MB limit' : null;

  onPendingFileChange(file: File | null): void {
    this.selectedFile = file;
  }

  openAttachment(): void {
    const url = this.currentAttachment?.url;
    if (!url) {
      this.alertNotificationService.warning('File URL not available');
      return;
    }
    this.projectFinanceService.attachmentPublicUrl(url).subscribe({
      next: (publicUrl: string) => {
        if (!publicUrl) {
          this.alertNotificationService.error('Error downloading file');
          return;
        }
        window.open(publicUrl, '_blank');
      },
      error: (error: any) => {
        console.error('Error downloading the receipt:', error);
        this.alertNotificationService.error('Error downloading file');
      }
    });
  }

  cancel(): void {
    this.routeService.projectsFinanceAnalysis(this.projectId);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertNotificationService.warning('Please fill in all required fields');
      return;
    }

    const value = this.form.getRawValue();
    const payload: ProjectFinanceOtherCostPayload = {
      category: value.category as ProjectOtherCostCategory,
      description: (value.description ?? '').trim(),
      amount: Number(value.amount),
      date: value.date as string,
      notes: value.notes?.trim() || null
    };
    const file = this.selectedFile;

    const request$ = this.isEditMode
      ? this.projectFinanceService.updateOtherCost(this.projectId, this.projectFinanceOtherCostId as number, payload)
      : this.projectFinanceService.createOtherCost(this.projectId, payload);

    this.isSaving = true;
    request$.pipe(
      concatMap(response => {
        const savedId = this.projectFinanceOtherCostId
          ?? this.projectFinanceService.readResponseKey<ProjectFinanceOtherCost>(response, 'projectFinanceOtherCost')?.projectFinanceOtherCostId;
        return file && savedId
          ? this.projectFinanceService.uploadOtherCostAttachment(this.projectId, savedId, file)
          : of(response);
      })
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.alertNotificationService.success(this.isEditMode ? 'Cost updated successfully' : 'Cost added successfully');
        this.routeService.projectsFinanceAnalysis(this.projectId);
      },
      error: (error) => {
        this.isSaving = false;
        console.error('Error saving other cost:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error saving cost'));
      }
    });
  }

  private loadOtherCost(): void {
    this.isLoading = true;
    this.projectFinanceService.listOtherCosts(this.projectId).subscribe({
      next: (response) => {
        this.isLoading = false;
        const cost = this.projectFinanceService.readResponseKey<ProjectFinanceOtherCost[]>(response, 'projectFinanceOtherCosts')
          ?.find(item => item.projectFinanceOtherCostId === this.projectFinanceOtherCostId);
        if (!cost) {
          this.alertNotificationService.error('Cost not found');
          return;
        }
        this.currentAttachment = cost.attachment;
        this.form.patchValue({
          category: cost.category,
          amount: cost.amount,
          description: cost.description,
          date: cost.date,
          notes: cost.notes
        });
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error loading other cost:', error);
        this.alertNotificationService.error(this.projectFinanceService.errorMessage(error, 'Error loading cost'));
      }
    });
  }
}
